/**
 * Issue #172 — 管理员 Web 员工管理 API 客户端（真实 /admin/employees*）。
 *
 * 全部经 apiClient：自动注入 Bearer、强制校验 response.ok + content-type，
 * 非 JSON / 非 2xx / 业务非零 code 一律抛 ApiError（带 HTTP 状态），UI 据此展示
 * 明确错误，绝不出现「Unexpected token」。不在任何错误路径塞假数据。
 */
import { apiClient } from './api';
import type { DeleteCaptchaProof } from './captcha-api';
import type {
  AdminDepartmentItem,
  AdminEmployeeListResult,
  AdminEmployeesListQuery,
  AdminEmployeeView,
  AvatarPresetsEnvelope,
  CreateEmployeeRequest,
  EmployeeCourseAssignment,
  ResetPasswordResult,
  ResetTrainingResult,
  TrainingRecoveryApplyResult,
  TrainingRecoveryDryRun,
  UpdateEmployeeRequest,
} from './types';

/**
 * Issue #197：员工列表分页 + 高级检索。返回 { items, pagination, columns } envelope。
 * 查询参数经 apiClient 序列化为 query string；空值由 encodeQuery 跳过。
 */
export function listEmployeesApi(
  query: AdminEmployeesListQuery = {},
): Promise<AdminEmployeeListResult> {
  return apiClient.get<AdminEmployeeListResult>(
    '/admin/employees',
    query as unknown as Record<string, unknown>,
  );
}

export function listDepartmentsApi(): Promise<AdminDepartmentItem[]> {
  return apiClient.get<AdminDepartmentItem[]>('/admin/departments');
}

export async function listAvatarPresetsApi(): Promise<AvatarPresetsEnvelope> {
  const data = await apiClient.get<AvatarPresetsEnvelope>('/admin/avatars/presets');
  return {
    urls: data?.urls ?? [],
    defaultUrl: data?.defaultUrl,
    configuredDefaultUrl: data?.configuredDefaultUrl,
  };
}

// ─── Issue #183：员工课程分配（真实 /admin/employees/:id/courses）。 ───
export function getEmployeeCoursesApi(id: string): Promise<EmployeeCourseAssignment> {
  return apiClient.get<EmployeeCourseAssignment>(
    `/admin/employees/${encodeURIComponent(id)}/courses`,
  );
}

export function setEmployeeCoursesApi(
  id: string,
  courseIds: string[],
): Promise<EmployeeCourseAssignment> {
  return apiClient.put<EmployeeCourseAssignment>(
    `/admin/employees/${encodeURIComponent(id)}/courses`,
    { courseIds },
  );
}

export function createEmployeeApi(body: CreateEmployeeRequest): Promise<AdminEmployeeView> {
  return apiClient.post<AdminEmployeeView>('/admin/employees', body);
}

export function updateEmployeeApi(
  id: string,
  body: UpdateEmployeeRequest,
): Promise<AdminEmployeeView> {
  return apiClient.patch<AdminEmployeeView>(`/admin/employees/${encodeURIComponent(id)}`, body);
}

export function softDeleteEmployeeApi(
  id: string,
  // Issue #235：删除需携带 6 位图形验证码，由后端一次性校验。
  // Issue #329：高危二次确认——额外携带当前管理员登录密码（后端 bcrypt 比对，绝不记录/回显）。
  proof: DeleteCaptchaProof & { currentPassword: string },
): Promise<{ id: string; deleted: boolean }> {
  return apiClient.delete<{ id: string; deleted: boolean }>(
    `/admin/employees/${encodeURIComponent(id)}`,
    proof,
  );
}

export function resetEmployeePasswordApi(id: string): Promise<ResetPasswordResult> {
  return apiClient.post<ResetPasswordResult>(
    `/admin/employees/${encodeURIComponent(id)}/reset-password`,
  );
}

export function resetEmployeeTrainingApi(
  id: string,
  courseId?: string,
): Promise<ResetTrainingResult> {
  return apiClient.post<ResetTrainingResult>(
    `/admin/employees/${encodeURIComponent(id)}/reset-training`,
    courseId ? { courseId } : {},
  );
}

// Issue #309：清理员工 SOP/ASR/AI 卡死状态（dry-run 诊断 + apply 执行）。
export function trainingRecoveryDryRunApi(id: string): Promise<TrainingRecoveryDryRun> {
  return apiClient.get<TrainingRecoveryDryRun>(
    `/admin/employees/${encodeURIComponent(id)}/training-recovery/dry-run`,
  );
}

export function trainingRecoveryApplyApi(id: string): Promise<TrainingRecoveryApplyResult> {
  return apiClient.post<TrainingRecoveryApplyResult>(
    `/admin/employees/${encodeURIComponent(id)}/training-recovery/apply`,
  );
}
